import {
  Shield,
  Star,
  Clock,
  Phone,
  Envelope,
  CurrencyDollar,
  Bank,
  FileText,
  ShieldCheck,
  Lock,
} from '@phosphor-icons/react';
import { Card } from 'antd';

interface TrustSignalsProps {
  variant?: 'full' | 'compact' | 'sidebar' | 'mobile';
  className?: string;
}

const TrustSignals: React.FC<TrustSignalsProps> = ({
  variant = 'full',
  className = '',
}) => {
  if (variant === 'compact') {
    return (
      <div
        className={`flex items-center justify-center gap-6 py-4 ${className}`}
      >
        <div className="flex items-center gap-2 text-sm">
          <Bank size={18} className="text-green-600" />
          <span className="font-medium text-gray-700">FDIC-Insured</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Shield size={18} className="text-blue-600" />
          <span className="font-medium text-gray-700">Bank-Level Security</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Phone size={18} className="text-gray-600" />
          <span className="font-medium text-gray-700">1-866-USA-TACH</span>
        </div>
      </div>
    );
  }

  if (variant === 'mobile') {
    return (
      <div
        className={`rounded-none border-[1px] border-bordergray bg-white p-4 ${className}`}
      >
        {/* Top row with FDIC logo and contact info */}
        <div className="mb-3 flex flex-wrap items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-center justify-center">
              <div className="mb-2 flex items-center gap-3">
                <h3 className="text-2xl font-bold text-gray-800">
                  FDIC-Insured
                </h3>{' '}
                <ShieldCheck size={24} className="text-blue-600" />
              </div>
              <p className="text-xs leading-relaxed text-gray-700">
                The TACH Trucker Funding Program Visa® Prepaid card is issued by{' '}
                <b>Pathward®, N.A., Member FDIC</b>, meaning your funds are
                automatically eligible for <b>FDIC insurance up to $250,000</b>{' '}
                and other protections.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Phone size={16} className="text-blue-600" />
            <a
              href="tel:1-866-872-8224"
              className="font-medium text-gray-700 transition-colors hover:text-blue-600"
            >
              1-866-USA-TACH
            </a>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Envelope size={16} className="text-blue-600" />
            <a
              href="mailto:support@tachusa.com"
              className="font-medium text-gray-700 transition-colors hover:text-blue-600"
            >
              support@tachusa.com
            </a>
          </div>
        </div>

        {/* Bottom row with legal links */}
        <div className="flex flex-wrap items-center justify-center gap-4 border-t border-gray-200 py-3">
          <a
            href="https://tachusa.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-gray-600 transition-colors hover:text-blue-600"
          >
            <FileText size={14} />
            Privacy Policy
          </a>
          <a
            href="https://tachusa.com/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-gray-600 transition-colors hover:text-blue-600"
          >
            <FileText size={14} />
            Terms & Conditions
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-start gap-4 border-t border-gray-200 pt-3">
          <div className="text-[10px] leading-tight text-gray-500">
            The TACH Trucker Funding Program Visa® Prepaid card is issued by
            Pathward®, N.A., Member FDIC, pursuant to a license from Visa U.S.A.
            Inc. Physical cards can be used everywhere Visa Debit Cards are
            accepted. Virtual cards can be used in stores everywhere contactless
            Visa debit cards are accepted. The cards have no ATM access. Funds
            loaded onto this card are held in a custodial account at Pathward,
            N.A. Your card is automatically eligible for FDIC insurance and
            other protections. TACH is not itself a bank or a FDIC-insured
            institution and the FDIC's deposit insurance coverage only protects
            against the failure of a FDIC-insured institution. Pathward,
            National Association is a FDIC-insured depository institution. Your
            funds are automatically eligible for FDIC pass-through insurance to
            the extent they are held by Pathward and otherwise meet the
            regulatory requirements for FDIC pass-through insurance. No other
            products or services referenced on this site are Pathward or Visa
            products or services.
          </div>
          <div className="text-[10px] leading-tight text-gray-500">
            TACH is a financial technology company, not an FDIC-insured bank.
            Money transmission services relating to the Passport Program are
            provided by Priority, directly or through its subsidiary Finxera,
            Inc. (NMLS #1168701) or its authorized affiliates and contractors.
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'sidebar') {
    return (
      <Card
        className={`rounded-none border-[1px] border-primary bg-white shadow-lg ${className}`}
      >
        <div className="space-y-6">
          {/* FDIC Insurance */}
          <div className="bg-white">
            <div className="mb-2 flex items-center gap-3">
              <h3 className="text-2xl font-bold text-gray-800">FDIC-Insured</h3>{' '}
              <ShieldCheck size={24} className="text-blue-600" />
            </div>
            <p className="text-xs leading-relaxed text-gray-700">
              The TACH Trucker Funding Program Visa® Prepaid card is issued by{' '}
              <b>Pathward®, N.A., Member FDIC</b>, meaning your funds are
              automatically eligible for <b>FDIC insurance up to $250,000</b>{' '}
              and other protections.
            </p>
          </div>

          {/* Security badges */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-xs">
              <Shield size={16} className="text-green-600" />
              <span className="text-gray-700">256-bit SSL</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Lock size={16} className="text-blue-600" />
              <span className="text-gray-700">Bank-Level Security</span>
            </div>
          </div>

          {/* Legal Links */}
          <div className="border-t border-gray-200 pt-4">
            <div className="space-y-2">
              <a
                href="https://tachusa.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-gray-700 transition-colors hover:text-blue-600"
              >
                <FileText size={14} className="text-gray-500" />
                Privacy Policy
              </a>
              <a
                href="https://tachusa.com/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-gray-700 transition-colors hover:text-blue-600"
              >
                <FileText size={14} className="text-gray-500" />
                Terms & Conditions
              </a>
            </div>
          </div>

          {/* Contact info */}
          <div className="border-t border-gray-200 pt-4">
            <p className="mb-2 text-xs font-semibold text-gray-700">
              Need Help?
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <Phone size={14} className="text-blue-600" />
                <a
                  href="tel:1-866-872-8224"
                  className="text-gray-700 transition-colors hover:text-blue-600"
                >
                  1-866-USA-TACH
                </a>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Envelope size={14} className="text-blue-600" />
                <a
                  href="mailto:support@tachusa.com"
                  className="text-gray-700 transition-colors hover:text-blue-600"
                >
                  support@tachusa.com
                </a>
              </div>
            </div>
          </div>

          {/* Disclaimers */}
          <div className="space-y-3 border-t border-gray-200 pt-4">
            <div className="text-[10px] leading-tight text-gray-500">
              The TACH Trucker Funding Program Visa® Prepaid card is issued by
              Pathward®, N.A., Member FDIC, pursuant to a license from Visa
              U.S.A. Inc. Physical cards can be used everywhere Visa Debit Cards
              are accepted. Virtual cards can be used in stores everywhere
              contactless Visa debit cards are accepted. The cards have no ATM
              access. Funds loaded onto this card are held in a custodial
              account at Pathward, N.A. Your card is automatically eligible for
              FDIC insurance and other protections. TACH is not itself a bank or
              a FDIC-insured institution and the FDIC's deposit insurance
              coverage only protects against the failure of a FDIC-insured
              institution. Pathward, National Association is a FDIC-insured
              depository institution. Your funds are automatically eligible for
              FDIC pass-through insurance to the extent they are held by
              Pathward and otherwise meet the regulatory requirements for FDIC
              pass-through insurance. No other products or services referenced
              on this site are Pathward or Visa products or services.
            </div>
            <div className="text-[10px] leading-tight text-gray-500">
              TACH is a financial technology company, not an FDIC-insured bank.
              Money transmission services relating to the Passport Program are
              provided by Priority, directly or through its subsidiary Finxera,
              Inc. (NMLS #1168701) or its authorized affiliates and contractors.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Full variant
  return (
    <div
      className={`rounded-xl bg-gradient-to-br from-gray-50 to-white p-8 shadow-xl ${className}`}
    >
      <div className="mx-auto max-w-6xl">
        {/* FDIC Insurance Banner */}
        <div className="mb-8 rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-green-100 p-6">
          <div className="flex items-center justify-center gap-4">
            <Bank size={32} className="text-green-700" />
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900">FDIC-Insured</h3>
              <p className="text-gray-700">
                The TACH Card is issued by <b>Pathward®, N.A., Member FDIC</b>,
                meaning your funds are eligible for{' '}
                <b>FDIC insurance up to $250,000</b> once you register your
                card.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="rounded-lg bg-white p-4 text-center shadow-md transition-shadow hover:shadow-lg">
            <div className="mb-2 flex justify-center text-green-600">
              <CurrencyDollar size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">$14,767</div>
            <div className="text-sm text-gray-600">
              Avg Annual Revenue Increase per Truck
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 text-center shadow-md transition-shadow hover:shadow-lg">
            <div className="mb-2 flex justify-center text-blue-600">
              <Clock size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">&lt; 4 hours</div>
            <div className="text-sm text-gray-600">Average Approval Time</div>
          </div>
          <div className="rounded-lg bg-white p-4 text-center shadow-md transition-shadow hover:shadow-lg">
            <div className="mb-2 flex justify-center text-yellow-500">
              <Star size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">4.8/5</div>
            <div className="text-sm text-gray-600">Customer Rating</div>
          </div>
        </div>

        {/* Security and Legal Footer */}
        <div className="border-t border-gray-200 pt-6">
          <div className="mb-4 flex items-center justify-center gap-8">
            <div className="flex items-center gap-2 text-sm">
              <Shield size={20} className="text-green-600" />
              <span className="font-medium text-gray-700">
                256-bit SSL Encryption
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <ShieldCheck size={20} className="text-blue-600" />
              <span className="font-medium text-gray-700">
                Bank-Level Security
              </span>
            </div>
          </div>

          {/* Legal Links */}
          <div className="mb-4 flex items-center justify-center gap-6">
            <a
              href="https://tachusa.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-blue-600"
            >
              <FileText size={16} />
              Privacy Policy
            </a>
            <a
              href="https://tachusa.com/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-blue-600"
            >
              <FileText size={16} />
              Terms & Conditions
            </a>
          </div>

          {/* Contact Info */}
          <div className="mb-6 flex items-center justify-center gap-8">
            <div className="flex items-center gap-2 text-sm">
              <Phone size={18} className="text-gray-600" />
              <a
                href="tel:1-866-872-8224"
                className="font-medium text-gray-700 transition-colors hover:text-blue-600"
              >
                1-866-USA-TACH
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Envelope size={18} className="text-gray-600" />
              <a
                href="mailto:support@tachusa.com"
                className="font-medium text-gray-700 transition-colors hover:text-blue-600"
              >
                support@tachusa.com
              </a>
            </div>
          </div>

          {/* Disclaimers */}
          <div className="space-y-2 rounded-lg bg-gray-50 p-4">
            <p className="text-xs leading-relaxed text-gray-500">
              <span className="font-semibold">[1]</span> The TACH Trucker
              Funding Program Visa® Prepaid card is issued by Pathward®, N.A.,
              Member FDIC, pursuant to a license from Visa U.S.A. Inc. Physical
              cards can be used everywhere Visa Debit Cards are accepted.
              Virtual cards can be used in stores everywhere contactless Visa
              debit cards are accepted. The cards have no ATM access. No other
              products or services referenced on this site are Pathward or Visa
              products or services.
            </p>
            <p className="text-xs leading-relaxed text-gray-500">
              <span className="font-semibold">[2]</span> TACH is a financial
              technology company, not an FDIC-insured bank. Money transmission
              services relating to the Passport Program are provided by
              Priority, directly or through its subsidiary Finxera, Inc. (NMLS
              #1168701) or its authorized affiliates and contractors.
            </p>
            <p className="text-xs leading-relaxed text-gray-500">
              <span className="font-semibold">[3]</span> Applying to TACH is
              completely free. Income deposits into the TACH account are subject
              to a fee.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustSignals;
